import { ChevronDownIcon } from '@chakra-ui/icons'
import {
  Avatar,
  Box,
  Container,
  Flex,
  HStack,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react'
import { CUIDEI_ROUTES } from 'constants/cuidei'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { FC, ReactNode, useEffect, useState } from 'react'
import { signOut } from 'redux/slices/auth-slice'
import { useAppDispatch, useAppSelector } from 'redux/store'

import CuideiLogo from './CuideiLogo'

interface CuideiHeaderProps {
  /** Optional right-aligned action (e.g. the "Começar agora" CTA on the landing). */
  action?: ReactNode
  maxW?: string
}

const navLinkStyle = {
  color: 'cuidei.ink',
  fontWeight: 600,
  fontSize: 'sm',
  whiteSpace: 'nowrap'
} as const

const NavTab: FC<{ href: string; children: ReactNode }> = ({ href, children }) => (
  <NextLink href={href} passHref legacyBehavior>
    <Link {...navLinkStyle} _hover={{ color: 'cuidei.60' }}>
      {children}
    </Link>
  </NextLink>
)

/**
 * Cuidei top bar.
 * - Logged out: logo + "Entrar" + optional landing CTA.
 * - Logged in: only the avatar/first-name menu (Consultas · Receitas e exames ·
 *   Minha conta · Sair). Keeps the bar uncluttered.
 */
const CuideiHeader: FC<CuideiHeaderProps> = ({ action, maxW = 'container.sm' }) => {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const profile = useAppSelector((state) => state.auth.profile)
  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn)
  // SSR landings skip the PersistGate, so persisted auth is already in the store
  // when React hydrates a logged-out server render. Read it only after mount.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const handleSignOut = () => {
    dispatch(signOut())
    router.push(CUIDEI_ROUTES.landing)
  }

  const authenticated = mounted && (!!profile || isLoggedIn)
  const firstName = profile?.firstName?.trim() || 'Minha conta'
  // Pathology pages (cuidei.com.br/<slug>, rewritten to /patologia/[slug]) keep
  // logged-out visitors on their pathology URL, so ads/analytics see one funnel.
  const pathologySlug = router.pathname?.startsWith('/patologia/[slug]') ? (router.query.slug as string) : null
  const landing = pathologySlug ? CUIDEI_ROUTES.pathologyLanding(pathologySlug) : CUIDEI_ROUTES.landing
  const logoDestination = authenticated ? CUIDEI_ROUTES.consultations : landing

  return (
    <Box as="header" bg="white" borderBottom="1px solid" borderColor="cuidei.20" py={3}>
      <Container maxW={maxW}>
        <Flex align="center" justify="space-between" gap={3}>
          {/* For authenticated patients, "home" is the patient area. Logged-out
              visitors still return to the public landing page. */}
          <NextLink href={logoDestination} passHref legacyBehavior>
            <Link
              _hover={{ textDecoration: 'none' }}
              aria-label={authenticated ? 'Ir para minhas consultas' : 'Página inicial Cuidei'}
            >
              <CuideiLogo />
            </Link>
          </NextLink>
          <Flex align="center" gap={{ base: 4, md: 6 }}>
            {authenticated ? (
              <>
                {/* Navbar deliberately minimal: no "+"/Nova consulta button
                    (removed per QA 2026-07-09 — caused mis-taps and layout
                    issues on mobile; the page CTAs cover starting a consult)
                    and no Consultas/Receitas tabs (they live in the avatar
                    menu + in-page tabs). */}
                <Menu placement="bottom-end" autoSelect={false}>
                  <MenuButton>
                    <HStack spacing={2}>
                      <Avatar size="sm" name={firstName} bg="cuidei.60" color="white" />
                      <Text {...navLinkStyle} display={{ base: 'none', sm: 'block' }}>
                        {profile?.firstName?.trim() || 'Conta'}
                      </Text>
                      <ChevronDownIcon color="cuidei.muted" />
                    </HStack>
                  </MenuButton>
                  <MenuList minW="180px">
                    <NextLink href={CUIDEI_ROUTES.consultations} passHref legacyBehavior>
                      <MenuItem as="a" fontSize="sm" fontWeight={600} color="cuidei.ink">
                        Consultas
                      </MenuItem>
                    </NextLink>
                    <NextLink href={CUIDEI_ROUTES.prescriptions} passHref legacyBehavior>
                      <MenuItem as="a" fontSize="sm" fontWeight={600} color="cuidei.ink">
                        Receitas e exames
                      </MenuItem>
                    </NextLink>
                    <MenuDivider />
                    <NextLink href={CUIDEI_ROUTES.account} passHref legacyBehavior>
                      <MenuItem as="a" fontSize="sm" fontWeight={600} color="cuidei.ink">
                        Minha conta
                      </MenuItem>
                    </NextLink>
                    <MenuDivider />
                    <MenuItem fontSize="sm" fontWeight={600} color="cuidei.muted" onClick={handleSignOut}>
                      Sair
                    </MenuItem>
                  </MenuList>
                </Menu>
              </>
            ) : (
              <>
                <NavTab href={CUIDEI_ROUTES.login}>Entrar</NavTab>
                {action}
              </>
            )}
          </Flex>
        </Flex>
      </Container>
    </Box>
  )
}

export default CuideiHeader
